import { EntityType } from '@frontend/common';
import { DefaultSecondaryButton } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { IGroup } from '@frontend/group/types';
import { CommonMessage, getCorrectTranslation, useLocale } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { ProductClient } from '@frontend/product/api';
import { IProduct } from '@frontend/product/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { LinkProductsModal } from '../../applications/modals/category-detail-modal/link-products-modal/link-products-modal.component';
import { ImageContainerWrapper } from '../../applications/modals/containers/image-wrapper/image-container-wrapper.components';
import { TranslationContainer } from '../../applications/modals/containers/translation-container/translations-container.component';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { useAppDispatch } from '../../redux/store';

const LINKED_PRODUCTS_LABEL = (
    <FormattedMessage
        id='attribute_detail_modal.linked_products'
        defaultMessage='Linked products'
    />
);

export const PRODUCT_CATEGORY_LINKED_LABEL = LINKED_PRODUCTS_LABEL;

export const ProductCategoryDetailsCard = ({ category }: { category: IGroup }) => {
    const { locale } = useLocale();
    return (
        <div className='flex w-full gap-3'>
            <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
                <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
                <PlatformDetailGrid
                    object={category}
                    cols={2}
                    order={['name', 'num_entities']}
                    keyOverwrite={
                        new Map([
                            ['name', CommonMessage.OBJECTS.CATEGORY.CATEGORY_NAME],
                            ['num_entities', LINKED_PRODUCTS_LABEL]
                        ])
                    }
                    valueOverwrite={
                        new Map<string, (obj: IGroup) => React.ReactNode>([['name', (obj) => (locale ? getCorrectTranslation(locale, obj, 'name') : obj.name)]])
                    }
                />
            </div>
            <ImageContainerWrapper
                entity={category}
                entity_type={EntityType.PRODUCT_CATEGORY}
            />
        </div>
    );
};

export const ProductCategoryGeneralSection = ({ category }: { category: IGroup }) => (
    <TranslationContainer
        ContainerTitle={CommonMessage.DETAIL.TRANSLATIONS}
        HeaderList={[CommonMessage.FORMS.NAME]}
        translations={['name']}
        object={category}
    />
);

export const ProductCategoryLinkedSection = ({ category }: { category: IGroup }) => {
    const dispatch = useAppDispatch();
    const { openModal } = useModals();
    const { locale } = useLocale();
    const LINK_PRODUCTS_MODAL_ID = 'link-category-products-modal';

    return (
        <ReactiveObjectList<IProduct>
            fetch={() =>
                ProductClient.fetchCategoryProducts({
                    account_id: category.account_id,
                    group_id: category.id
                })
            }
            eventListener={EntityType.GROUP}
            dispatch={dispatch}
            columns={{
                columns: ['name', 'sku', 'ean'],
                headerOverwrite: new Map([
                    ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
                    ['ean', CommonMessage.OBJECTS.PRODUCT.EAN]
                ]),
                customCells: new Map([
                    [
                        'name',
                        (row) => (
                            <td
                                key={row.row.original.id}
                                className={defaultCellClassName}>
                                <PlatformObjectBadge
                                    entityType={EntityType.PRODUCT}
                                    id={row.row.original.id}
                                    display={locale ? getCorrectTranslation(locale, row.row.original, 'name') : row.row.original.name}
                                />
                            </td>
                        )
                    ]
                ])
            }}
            type={EntityType.MEMBER}
            tableProps={{
                defaultHeight: true,
                customFooter: (
                    <div>
                        <DefaultSecondaryButton
                            text={
                                <FormattedMessage
                                    id='category-detail-modal.link-products-button'
                                    defaultMessage='Link products'
                                />
                            }
                            onClick={() => openModal(LINK_PRODUCTS_MODAL_ID, () => <LinkProductsModal groupId={category.id} />)}
                        />
                    </div>
                )
            }}
        />
    );
};
